// src/hooks/useRateLimit.js  —  ohapotato v2026.10.36
import { useState, useRef, useCallback } from 'react';
//
// Frontend request rate limiter. Two layers of protection:
//
//  1. SESSION limit  — tracked in memory (sessionStorage key).
//     Resets when the browser tab closes. Hard cap: 15 requests per session.
//     Purpose: prevents bots / automated scripts hammering a single tab.
//
//  2. DAILY limit    — tracked in localStorage with a UTC date stamp.
//     Resets at midnight UTC. Cap: 20 requests per day per browser.
//     Purpose: stops a single user from spending your entire API budget.
//
//  3. COOLDOWN       — 8-second minimum between consecutive requests.
//     Purpose: prevents rapid double-clicks and accidental spam.
//
// Authenticated users get a higher daily cap (50 vs 20) because they have
// an accountable identity. Premium users are uncapped.
//
// All limits are enforced on the frontend only — add server-side enforcement
// (e.g. Upstash Redis rate limiter on a Netlify Edge Function) for production.

const SESSION_KEY   = 'ohp_session_requests';
const DAILY_KEY     = 'ohp_daily_requests';
const COOLDOWN_MS   = 8_000;

const LIMITS = {
  guest: { session: 8,  daily: 12 },
  auth:  { session: 60, daily: 120 }, // authenticated users get generous limits
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getTodayKey() {
  return new Date().toISOString().slice(0, 10); // "2026-03-09"
}

function getSessionCount() {
  try {
    return parseInt(sessionStorage.getItem(SESSION_KEY) || '0', 10);
  } catch { return 0; }
}

function setSessionCount(n) {
  try { sessionStorage.setItem(SESSION_KEY, String(n)); } catch {}
}

function getDailyRecord() {
  try {
    const raw = localStorage.getItem(DAILY_KEY);
    if (!raw) return { date: '', count: 0 };
    return JSON.parse(raw);
  } catch { return { date: '', count: 0 }; }
}

function setDailyRecord(record) {
  try { localStorage.setItem(DAILY_KEY, JSON.stringify(record)); } catch {}
}

function getDailyCount() {
  const rec = getDailyRecord();
  if (rec.date !== getTodayKey()) return 0;   // new day — reset
  return rec.count;
}

function incrementDaily() {
  const today = getTodayKey();
  const rec   = getDailyRecord();
  const count = (rec.date === today ? rec.count : 0) + 1;
  setDailyRecord({ date: today, count });
  return count;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useRateLimit({ isAuthenticated = false } = {}) {
  const tier    = isAuthenticated ? 'auth' : 'guest';
  const limits  = LIMITS[tier];

  const lastReqRef = useRef(0);   // timestamp of last request
  const [blocked, setBlocked]   = useState(null);  // null | { reason, resetsIn }

  // Returns null if request is allowed, or a string reason if blocked
  const check = useCallback(() => {
    const now = Date.now();

    // Cooldown check
    const sinceLast = now - lastReqRef.current;
    if (sinceLast < COOLDOWN_MS && lastReqRef.current > 0) {
      const waitSecs = Math.ceil((COOLDOWN_MS - sinceLast) / 1000);
      return { reason: `Please wait ${waitSecs} second${waitSecs !== 1 ? 's' : ''} before searching again.`, resetsIn: waitSecs };
    }

    // Session check
    const sessionCount = getSessionCount();
    if (sessionCount >= limits.session) {
      return {
        reason: `You've reached the session limit of ${limits.session} searches. Open a new tab to continue.`,
        resetsIn: null,
      };
    }

    // Daily check
    const dailyCount = getDailyCount();
    if (dailyCount >= limits.daily) {
      const midnight = new Date();
      midnight.setUTCHours(24, 0, 0, 0);
      const hoursLeft = Math.ceil((midnight - now) / 3_600_000);
      return {
        reason: `Daily search limit reached (${limits.daily} searches). Resets in ~${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''}.`,
        resetsIn: hoursLeft * 3600,
      };
    }

    return null;  // all clear
  }, [limits]);

  // Call before every API request. Returns true if allowed, false if blocked.
  const consume = useCallback(() => {
    const block = check();
    if (block) {
      setBlocked(block);
      return false;
    }
    // Record the request
    lastReqRef.current = Date.now();
    setSessionCount(getSessionCount() + 1);
    incrementDaily();
    setBlocked(null);
    return true;
  }, [check]);

  // How many searches remain this session / today
  const remaining = {
    session: Math.max(0, limits.session - getSessionCount()),
    daily:   Math.max(0, limits.daily   - getDailyCount()),
  };

  const clearBlock = useCallback(() => setBlocked(null), []);

  return { consume, blocked, clearBlock, remaining, tier };
}
